'use client';
import React from 'react';

interface ICastLogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showText?: boolean;
  className?: string;
}

export default function ICastLogo({ size = 'md', showText = true, className = '' }: ICastLogoProps) {
  const sizeMap = {
    sm: { emblem: 28, text: 'text-lg', gap: 'gap-2' },
    md: { emblem: 36, text: 'text-xl', gap: 'gap-2.5' },
    lg: { emblem: 48, text: 'text-2xl', gap: 'gap-3' },
    xl: { emblem: 64, text: 'text-3xl', gap: 'gap-4' },
  };
  const s = sizeMap[size];

  return (
    <div className={`flex items-center ${s.gap} ${className}`}>
      {/* Emblem / Logo Mark */}
      <div className="relative flex-shrink-0" style={{ width: s.emblem, height: s.emblem }}>
        <svg
          width={s.emblem}
          height={s.emblem}
          viewBox="0 0 64 64"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Outer ring — broadcast signal arc */}
          <circle cx="32" cy="32" r="30" stroke="url(#ring-gradient)" strokeWidth="2" opacity="0.4" />
          {/* Signal arcs */}
          <path
            d="M 14 44 A 22 22 0 0 1 14 20"
            stroke="url(#arc-gradient)"
            strokeWidth="2.5"
            strokeLinecap="round"
            fill="none"
          />
          <path
            d="M 50 44 A 22 22 0 0 0 50 20"
            stroke="url(#arc-gradient)"
            strokeWidth="2.5"
            strokeLinecap="round"
            fill="none"
          />
          <path
            d="M 8 50 A 32 32 0 0 1 8 14"
            stroke="url(#arc-gradient)"
            strokeWidth="1.5"
            strokeLinecap="round"
            fill="none"
            opacity="0.5"
          />
          <path
            d="M 56 50 A 32 32 0 0 0 56 14"
            stroke="url(#arc-gradient)"
            strokeWidth="1.5"
            strokeLinecap="round"
            fill="none"
            opacity="0.5"
          />
          {/* Center hexagon body */}
          <path
            d="M 32 10 L 46 18.5 L 46 35.5 L 32 44 L 18 35.5 L 18 18.5 Z"
            fill="url(#hex-gradient)"
          />
          {/* Play triangle / broadcast symbol */}
          <path
            d="M 27 22 L 27 38 L 40 30 Z"
            fill="white"
            opacity="0.95"
          />
          {/* Live dot */}
          <circle cx="44" cy="18" r="5" fill="#e63946" />
          <circle cx="44" cy="18" r="3" fill="#ff6b6b" />

          <defs>
            <linearGradient id="ring-gradient" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
              <stop stopColor="#e63946" />
              <stop offset="1" stopColor="#f4a261" />
            </linearGradient>
            <linearGradient id="arc-gradient" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
              <stop stopColor="#e63946" />
              <stop offset="1" stopColor="#f4a261" />
            </linearGradient>
            <linearGradient id="hex-gradient" x1="18" y1="10" x2="46" y2="44" gradientUnits="userSpaceOnUse">
              <stop stopColor="#e63946" />
              <stop offset="1" stopColor="#c1121f" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {showText && (
        <div className="flex flex-col leading-none">
          <span className={`font-bold tracking-tight text-gradient-red ${s.text}`} style={{ fontFamily: 'DM Sans, sans-serif' }}>
            I-Cast
          </span>
          <span className="text-[10px] font-medium tracking-widest uppercase text-zinc-500 mt-0.5">
            Broadcaster Studio
          </span>
        </div>
      )}
    </div>
  );
}